import React, { useState } from 'react';
import API from '../lib/api';
import { Calculator, DownloadSimple, SpinnerGap, Info, CheckCircle, Warning } from '@phosphor-icons/react';

const VAK_LABELS = {
  '00': 'Omzet vrijgesteld / 0% / uitvoer / ICL',
  '01': 'Omzet 6%',
  '02': 'Omzet 12%',
  '03': 'Omzet 21%',
  '44': 'Diensten aan EU-klanten (geen BTW)',
  '45': 'Intracommunautaire leveringen (EU)',
  '46': 'Verkoop aan particulieren in EU',
  '47': 'Uitvoer buiten EU',
  '48': 'Creditnota\'s op vak 45',
  '49': 'Creditnota\'s op vak 44-47',
  '54': 'Verschuldigde BTW op omzet (01-03)',
  '55': 'Verschuldigde BTW op intracommunautaire aankopen',
  '56': 'Verschuldigde BTW op medecontractant',
  '57': 'Verschuldigde BTW op vak 88',
  '59': 'Aftrekbare BTW op aankopen',
  '81': 'Aankopen handelsgoederen, grondstoffen',
  '82': 'Aankopen diensten en diverse goederen',
  '83': 'Aankopen bedrijfsmiddelen (investeringen)',
  '84': 'Creditnota\'s ontvangen op 81-83',
  '85': 'Andere creditnota\'s ontvangen',
  '86': 'Intracommunautaire aankopen (EU)',
  '87': 'Andere inkomende - verlegd / buitenland',
  '88': 'Ontvangen diensten van buitenland',
  '91': 'BTW op 88 te voldoen bij volgende aangifte',
};

const fmt = (n) => new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);

export default function BtwAangiftePage() {
  const now = new Date();
  const [activeTab, setActiveTab] = useState('btw'); // 'btw' | 'ic' | 'kl'
  const [jaar, setJaar] = useState(now.getFullYear());
  const [periode, setPeriode] = useState('Q' + (Math.floor(now.getMonth() / 3) + 1));
  const [drempel, setDrempel] = useState(250);
  const [aangifte, setAangifte] = useState(null);
  const [icOpgave, setIcOpgave] = useState(null);
  const [klListing, setKlListing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const laad = async () => {
    setLoading(true); setError(null);
    try {
      if (activeTab === 'btw') {
        setAangifte(null);
        const r = await API.get(`/btw-aangifte?jaar=${jaar}&periode=${periode}`);
        setAangifte(r.data);
      } else if (activeTab === 'ic') {
        setIcOpgave(null);
        const r = await API.get(`/ic-opgave?jaar=${jaar}&periode=${periode}`);
        setIcOpgave(r.data);
      } else {
        setKlListing(null);
        const r = await API.get(`/klantenlisting?jaar=${jaar}&drempel=${drempel}`);
        setKlListing(r.data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Fout bij ophalen');
    }
    setLoading(false);
  };

  const downloadXml = () => {
    let endpoint, params;
    if (activeTab === 'btw') { endpoint = 'btw-aangifte/intervat-xml'; params = `jaar=${jaar}&periode=${periode}`; }
    else if (activeTab === 'ic') { endpoint = 'ic-opgave/intervat-xml'; params = `jaar=${jaar}&periode=${periode}`; }
    else { endpoint = 'klantenlisting/intervat-xml'; params = `jaar=${jaar}&drempel=${drempel}`; }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/${endpoint}?${params}`;
    const a = document.createElement('a');
    a.href = url; a.target = '_blank'; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Groepeer vakken: Omzet (00-49), Verschuldigd (54-57), Aankopen (81-91), Aftrekbaar (59)
  const groups = aangifte ? [
    { titel: 'Uitgaande handelingen (omzet)', keys: ['00','01','02','03','44','45','46','47','48','49'] },
    { titel: 'Verschuldigde BTW', keys: ['54','55','56','57'] },
    { titel: 'Inkomende handelingen (aankopen)', keys: ['81','82','83','84','85','86','87','88'] },
    { titel: 'Aftrekbare BTW', keys: ['59','91'] },
  ] : [];

  return (
    <div className="space-y-5" data-testid="btw-aangifte-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#002FA7] flex items-center gap-2"><Calculator size={28} weight="bold" /> BTW-aangifte & Intracommunautaire Opgave</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        <button className={`text-sm font-bold pb-2 -mb-[1px] border-b-2 ${activeTab === 'btw' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          onClick={() => { setActiveTab('btw'); setError(null); }} data-testid="tab-btw">
          Periodieke BTW-aangifte
        </button>
        <button className={`text-sm font-bold pb-2 -mb-[1px] border-b-2 ${activeTab === 'ic' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          onClick={() => { setActiveTab('ic'); setError(null); }} data-testid="tab-ic">
          Intracommunautaire Opgave (723)
        </button>
        <button className={`text-sm font-bold pb-2 -mb-[1px] border-b-2 ${activeTab === 'kl' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          onClick={() => { setActiveTab('kl'); setError(null); }} data-testid="tab-kl">
          Klantenlisting (725)
        </button>
      </div>

      {/* Periode selector */}
      <div className="section-card">
        <div className="p-5">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Jaar</label>
              <input type="number" className="field-input" style={{width: 100}} value={jaar} onChange={e => setJaar(parseInt(e.target.value))} data-testid="btw-jaar" />
            </div>
            {activeTab !== 'kl' ? (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Periode</label>
                <select className="field-input" style={{width: 160}} value={periode} onChange={e => setPeriode(e.target.value)} data-testid="btw-periode">
                  <optgroup label="Kwartaal">
                    <option value="Q1">Q1 (jan-mrt)</option>
                    <option value="Q2">Q2 (apr-jun)</option>
                    <option value="Q3">Q3 (jul-sep)</option>
                    <option value="Q4">Q4 (okt-dec)</option>
                  </optgroup>
                  <optgroup label="Maand">
                    {Array.from({length: 12}).map((_, i) => {
                      const m = String(i+1).padStart(2,'0');
                      const names = ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'];
                      return <option key={m} value={m}>{m} — {names[i]}</option>;
                    })}
                  </optgroup>
                </select>
              </div>
            ) : (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Drempel (€)</label>
                <input type="number" className="field-input" style={{width: 120}} value={drempel} onChange={e => setDrempel(parseFloat(e.target.value) || 0)} data-testid="kl-drempel" />
              </div>
            )}
            <button className="btn btn-primary" onClick={laad} disabled={loading} data-testid="btw-bereken">
              {loading ? <><SpinnerGap size={14} className="animate-spin" /> Berekenen...</> : <><Calculator size={14} weight="bold" /> Bereken {activeTab === 'btw' ? 'aangifte' : activeTab === 'ic' ? 'opgave' : 'listing'}</>}
            </button>
            {((activeTab === 'btw' && aangifte) || (activeTab === 'ic' && icOpgave) || (activeTab === 'kl' && klListing)) && (
              <button className="btn bg-[#D97706] text-white font-bold hover:bg-[#B45309] inline-flex items-center gap-1.5" onClick={downloadXml} data-testid="btw-download-xml">
                <DownloadSimple size={14} weight="bold" /> Download Intervat XML
              </button>
            )}
          </div>

          <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
            <Info size={12} weight="bold" />
            {activeTab === 'btw'
              ? 'De aangifte wordt berekend op basis van alle verwerkte inkomende en uitgaande documenten in de gekozen periode.'
              : activeTab === 'ic'
              ? 'Groepering per EU-klant (niet-BE BTW-nr) van alle intracommunautaire leveringen. Creditnota\'s worden automatisch afgetrokken.'
              : `Jaarlijkse lijst van alle Belgische BTW-plichtige klanten met totale omzet > €${drempel}. Indienen vóór 31 maart van het volgende jaar.`}
            {' '}Upload de XML op <a href="https://financien.belgium.be/nl/E-services/Intervat" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] underline">Intervat (MyMinFin)</a>.
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2" data-testid="btw-error">
          <Warning size={16} weight="bold" /> {error}
        </div>
      )}

      {/* ── BTW aangifte tab ── */}
      {activeTab === 'btw' && aangifte && (
        <>
          <div className={`p-5 border-l-4 ${aangifte.saldo.vak === '71' ? 'bg-red-50 border-red-500' : 'bg-green-50 border-green-500'}`} data-testid="btw-saldo">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                  Saldo aangifte {aangifte.jaar} / {aangifte.periode}
                </div>
                <div className={`text-3xl font-black ${aangifte.saldo.vak === '71' ? 'text-red-700' : 'text-green-700'}`}>
                  {fmt(aangifte.saldo.bedrag)}
                </div>
                <div className="text-sm font-bold text-gray-700">
                  {aangifte.saldo.label} (Vak {aangifte.saldo.vak})
                </div>
              </div>
              <div className="text-xs text-gray-500 text-right">
                <div><strong>{aangifte.aantal_uitgaand}</strong> uitgaande facturen</div>
                <div><strong>{aangifte.aantal_creditnotas}</strong> creditnota's</div>
                <div><strong>{aangifte.aantal_inkomend}</strong> inkomende facturen</div>
                <div className="mt-1 text-[10px]">Periode: {aangifte.start_date} → {aangifte.eind_date}</div>
              </div>
            </div>
          </div>

          {groups.map(g => (
            <div className="section-card" key={g.titel}>
              <div className="section-header"><span className="section-title">{g.titel}</span></div>
              <div className="p-0">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{width: 80}}>Vak</th>
                      <th>Omschrijving</th>
                      <th className="right" style={{width: 180}}>Bedrag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.keys.map(k => {
                      const val = aangifte.vakken[k] || 0;
                      const nonZero = Math.abs(val) > 0.005;
                      return (
                        <tr key={k} className={nonZero ? '' : 'text-gray-400'} data-testid={`btw-vak-${k}`}>
                          <td className="font-mono font-bold">{k}</td>
                          <td className="text-xs">{VAK_LABELS[k] || '-'}</td>
                          <td className={`right font-bold ${nonZero ? 'text-[#002FA7]' : ''}`}>{fmt(val)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div className="p-4 bg-blue-50 border border-blue-200 text-xs text-blue-800">
            <CheckCircle size={14} weight="bold" className="inline mr-1" />
            <strong>Klaar om in te dienen?</strong> Download de XML, log in op Intervat (<a href="https://financien.belgium.be/nl/E-services/Intervat" target="_blank" rel="noopener noreferrer" className="underline">link</a>), kies "Aangifte via bestand versturen" en upload het XML-bestand.
          </div>
        </>
      )}

      {/* ── IC opgave tab ── */}
      {activeTab === 'ic' && icOpgave && (
        <>
          <div className="p-5 border-l-4 bg-blue-50 border-[#002FA7]" data-testid="ic-summary">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                  IC Opgave {icOpgave.jaar} / {icOpgave.periode}
                </div>
                <div className="text-3xl font-black text-[#002FA7]">{fmt(icOpgave.totaal)}</div>
                <div className="text-sm font-bold text-gray-700">
                  Totaal intracommunautaire leveringen aan <strong>{icOpgave.aantal_klanten}</strong> EU-klant(en)
                </div>
              </div>
              <div className="text-xs text-gray-500 text-right">
                <div className="text-[10px]">Periode: {icOpgave.start_date} → {icOpgave.eind_date}</div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header"><span className="section-title">Leveringen per EU-klant</span></div>
            <div className="p-0">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{width: 60}}>Land</th>
                    <th style={{width: 180}}>BTW-nummer</th>
                    <th>Klant</th>
                    <th style={{width: 80}}>Code</th>
                    <th style={{width: 80}}>Aantal</th>
                    <th className="right" style={{width: 160}}>Totaal</th>
                  </tr>
                </thead>
                <tbody>
                  {icOpgave.clients.length === 0 && (
                    <tr><td colSpan="6" className="text-center text-gray-400 py-8">Geen intracommunautaire leveringen in deze periode</td></tr>
                  )}
                  {icOpgave.clients.map((c, i) => (
                    <tr key={`${c.btw_full}-${c.code}`} data-testid={`ic-row-${i}`}>
                      <td><span className="text-[10px] font-bold px-2 py-0.5 bg-[#002FA7] text-white">{c.landcode}</span></td>
                      <td className="font-mono text-xs">{c.btw_full}</td>
                      <td className="text-xs font-medium">{c.klant_naam}</td>
                      <td>
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-gray-100" title={
                          c.code === 'L' ? 'Goederenlevering' : c.code === 'S' ? 'Dienst' : c.code === 'T' ? 'Driehoekstransactie' : ''
                        }>
                          {c.code} — {c.code === 'L' ? 'Goederen' : c.code === 'S' ? 'Diensten' : c.code === 'T' ? 'Driehoek' : 'Andere'}
                        </span>
                      </td>
                      <td className="text-xs">{c.aantal_facturen}</td>
                      <td className="right font-bold text-[#002FA7]">{fmt(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-4 bg-yellow-50 border border-yellow-200 text-xs text-yellow-800">
            <Warning size={14} weight="bold" className="inline mr-1" />
            <strong>Let op — codes:</strong> L = goederenlevering, S = dienst volgens art. 196, T = driehoekstransactie. Wijzig de code per klant via <em>Klanten → IC code</em>. Standaard is "L". Bedragen onder of gelijk aan 0 worden niet meegenomen.
          </div>
        </>
      )}

      {/* ── Klantenlisting tab ── */}
      {activeTab === 'kl' && klListing && (
        <>
          <div className="p-5 border-l-4 bg-purple-50 border-purple-500" data-testid="kl-summary">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                  Klantenlisting {klListing.jaar}
                </div>
                <div className="text-3xl font-black text-purple-700">{fmt(klListing.totaal_omzet)}</div>
                <div className="text-sm font-bold text-gray-700">
                  Totaal omzet bij <strong>{klListing.aantal_klanten}</strong> Belgische BTW-plichtige klant(en) boven drempel €{klListing.drempel}
                </div>
              </div>
              <div className="text-xs text-gray-500 text-right">
                <div>Totale BTW: <strong>{fmt(klListing.totaal_btw)}</strong></div>
                <div className="mt-1 text-[10px]">Indieningsdeadline: 31 maart {klListing.jaar + 1}</div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header"><span className="section-title">Klanten — Omzet boven drempel</span></div>
            <div className="p-0 overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{width: 40}}>#</th>
                    <th style={{width: 160}}>BTW-nummer</th>
                    <th>Klant</th>
                    <th style={{width: 80}}>Facturen</th>
                    <th className="right" style={{width: 160}}>Omzet (excl.)</th>
                    <th className="right" style={{width: 160}}>BTW</th>
                  </tr>
                </thead>
                <tbody>
                  {klListing.clients.length === 0 && (
                    <tr><td colSpan="6" className="text-center text-gray-400 py-8">Geen Belgische klanten boven drempel in {klListing.jaar}</td></tr>
                  )}
                  {klListing.clients.map((c, i) => (
                    <tr key={c.btw_nummer} data-testid={`kl-row-${i}`}>
                      <td className="text-xs text-gray-400">{i+1}</td>
                      <td className="font-mono text-xs">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#002FA7] text-white mr-1">BE</span>
                        {c.btw_nummer}
                      </td>
                      <td className="text-xs font-medium">{c.klant_naam}</td>
                      <td className="text-xs">{c.aantal_facturen}</td>
                      <td className="right font-bold text-purple-700">{fmt(c.omzet_excl)}</td>
                      <td className="right text-xs">{fmt(c.btw_bedrag)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 text-xs text-blue-800">
            <CheckCircle size={14} weight="bold" className="inline mr-1" />
            <strong>Jaarlijkse BTW-verplichting:</strong> alle Belgische BTW-plichtige klanten waaraan u voor meer dan €{klListing.drempel} (excl BTW) verkocht heeft moeten worden opgegeven. Download de XML en upload op Intervat vóór <strong>31 maart {klListing.jaar + 1}</strong>. Creditnota's zijn al verrekend.
          </div>
        </>
      )}
    </div>
  );
}
